import React, { useState, useEffect } from 'react'
import { useForm, useFieldArray, Controller } from 'react-hook-form'
import { useQuery, useMutation } from '@apollo/client'
import { useOutletContext } from 'react-router'

import {
  BlockQuote,
  CardBox,
  Container,
  Link,
  Stack,
  Text,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  useAlert,
  shortWait,
  IconButton,
  Divider,
  FormHelperText,
  LinearProgress,
  NotificationDialog,
} from '../components'
import { Trash, Plus } from '../components/icons/icons'
import { queries, mutations } from '../graphql'

export function Successor() {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [allocationErrorOpen, setAllocationErrorOpen] = useState(false)

  const [{ Alert, alertProps }, { setAlert }] = useAlert()

  const { donorFundId } = useOutletContext() ?? {}

  const showAlert = (errorMessage) => {
    setAlert({
      message: 'Something went wrong!',
      severity: 'error',
      timeout: shortWait,
      error: errorMessage,
    })
  }

  const { data, loading, error, refetch } = useQuery(queries.funds.getFundSuccessionInstructions, {
    variables: { fundId: donorFundId },
    skip: !donorFundId,
    errorPolicy: 'all',
  })

  const [saveFundSuccessionInstructions] = useMutation(mutations.funds.saveFundSuccessionInstructions, {
    onCompleted: () => {
      setAlert({
        message: 'Succession instructions saved successfully!',
        severity: 'success',
        timeout: shortWait,
      })
      refetch()
    },
    onError: (instructionError) => {
      console.error('Error saving succession instructions:', instructionError)
      showAlert('Error saving succession instructions. Please try again.')
    },
  })

  const [deleteFundSuccessionInstructions] = useMutation(mutations.funds.deleteFundSuccessionInstructions, {
    onCompleted: () => {
      setAlert({
        message: 'Succession instructions deleted successfully!',
        severity: 'success',
        timeout: shortWait,
      })
      refetch()
    },
    onError: (deleteError) => {
      console.error('Error deleting succession instructions:', deleteError)
      showAlert('Error deleting succession instructions. Please try again.')
    },
  })

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    watch,
    getValues,
    setError,
    clearErrors,
    reset,
  } = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: {
      successors: [],
      charities: [],
    },
  })

  const {
    fields: successorFields,
    append: appendSuccessor,
    remove: removeSuccessor,
  } = useFieldArray({
    control,
    name: 'successors',
  })

  const {
    fields: charityFields,
    append: appendCharity,
    remove: removeCharity,
  } = useFieldArray({
    control,
    name: 'charities',
  })

  useEffect(() => {
    if (data?.getFundSuccessionInstructions) {
      const instructions = data.getFundSuccessionInstructions

      const formData = {
        successors: instructions.successors.map((successor) => ({
          firstName: successor.firstName,
          lastName: successor.lastName,
          email: successor.email,
          address: successor.address,
          phoneNumber: successor.phoneNumber,
          relationshipToYou: successor.relationshipToYou,
          dateOfBirth: new Date(successor.dateOfBirth).toISOString().split('T')[0],
          allocationPercentage: successor.allocationPercentage.toString(),
          alternativeAction: successor.alternativeAction,
        })),
        charities: instructions.charities.map((charity) => ({
          charityName: charity.charityName,
          registrationNumber: charity.registrationNumber,
          designation: charity.designation,
          allocationPercentage: charity.allocationPercentage.toString(),
          timeFrame: charity.timeFrame,
          timeFrameMonths: charity.timeFrameMonths?.toString() || '',
          timeFrameYears: charity.timeFrameYears?.toString() || '',
          alternativeAction: charity.alternativeAction,
        })),
      }
      reset(formData)
    }
  }, [data, reset])

  const addSuccessor = () => {
    appendSuccessor({
      firstName: '',
      lastName: '',
      email: '',
      address: '',
      phoneNumber: '',
      relationshipToYou: '',
      dateOfBirth: '',
      allocationPercentage: '',
      alternativeAction: 'allocate_pro_rata_successors',
    })
  }

  const addCharity = () => {
    appendCharity({
      charityName: '',
      registrationNumber: '',
      designation: '',
      allocationPercentage: '',
      timeFrame: 'immediately',
      timeFrameMonths: '',
      timeFrameYears: '',
      alternativeAction: 'allocate_pro_rata_charities',
    })
  }

  const validateAllocations = (allocations) => {
    const successorTotal = allocations.successors.reduce((sum, s) => sum + (parseFloat(s.allocationPercentage) || 0), 0)
    const charityTotal = allocations.charities.reduce((sum, c) => sum + (parseFloat(c.allocationPercentage) || 0), 0)
    const total = successorTotal + charityTotal

    if (total !== 100) {
      return false
    }

    return true
  }

  const onSubmit = async (submit) => {
    if (!validateAllocations(submit)) {
      setAllocationErrorOpen(true)
      return
    }

    setIsSubmitting(true)
    try {
      const successionData = {
        successors: submit.successors.map((successor) => ({
          firstName: successor.firstName,
          lastName: successor.lastName,
          email: successor.email,
          address: successor.address,
          phoneNumber: successor.phoneNumber,
          relationshipToYou: successor.relationshipToYou,
          dateOfBirth: successor.dateOfBirth,
          allocationPercentage: parseInt(successor.allocationPercentage, 10),
          alternativeAction: successor.alternativeAction,
        })),
        charities: submit.charities.map((charity) => ({
          charityName: charity.charityName,
          registrationNumber: charity.registrationNumber,
          designation: charity.designation,
          allocationPercentage: parseInt(charity.allocationPercentage, 10),
          timeFrame: charity.timeFrame,
          timeFrameMonths: charity.timeFrameMonths ? parseInt(charity.timeFrameMonths, 10) : null,
          timeFrameYears: charity.timeFrameYears ? parseInt(charity.timeFrameYears, 10) : null,
          alternativeAction: charity.alternativeAction,
        })),
      }

      await saveFundSuccessionInstructions({
        variables: {
          fundId: donorFundId,
          data: successionData,
        },
      })
    } catch (saveError) {
      console.error('Error saving succession instructions:', saveError)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDeleteConfirm = async () => {
    setIsSubmitting(true)
    setDeleteDialogOpen(false)
    try {
      await deleteFundSuccessionInstructions({
        variables: { fundId: donorFundId },
      })
    } catch (deleteError) {
      console.error('Error saving succession instructions:', deleteError)
    } finally {
      setIsSubmitting(false)
      reset({
        successors: [],
        charities: [],
      })
    }
  }

  if (loading) {
    return (
      <CardBox>
        <Container maxWidth="lg" sx={{ mb: 4 }}>
          <Stack spacing={3}>
            <Text.H1>Loading Succession Instructions...</Text.H1>
            <LinearProgress />
          </Stack>
        </Container>
      </CardBox>
    )
  }

  if (!donorFundId || error) {
    return (
      <CardBox>
        <Container maxWidth="lg" sx={{ mb: 4 }}>
          <Stack spacing={3}>
            <Text.H1>Succession Request</Text.H1>
            <Text.H5 color="error">Error loading succession instructions: {error.message}</Text.H5>
          </Stack>
        </Container>
      </CardBox>
    )
  }

  return (
    <CardBox>
      <Container maxWidth="lg" sx={{ mb: 4 }}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <fieldset disabled={isSubmitting} style={{ display: 'contents', border: 0, p: 0, m: 0 }}>
            <Stack spacing={3}>
              <Text.H4>Succession Request</Text.H4>

              <Text.AltBody>
                Currently, you are the advisor of this Giving Fund. In the event of your death or incapacity, there are
                two ways to divide the remaining assets in the Giving Fund: naming one or more successors or allocating
                funds to one or more charities. You may also choose a combination of these two options. The combined
                allocation cannot exceed 100%.
              </Text.AltBody>

              <Stack spacing={4}>
                {/* Successor Advisor Section */}
                <Box>
                  <Text.H5 sx={{ mb: 2 }}>Successor Advisor</Text.H5>
                  <Text.Body sx={{ mb: 3 }}>
                    You may elect one or more individuals to be the Successor Advisor of this Giving Fund; that is, the
                    person who replaces you as the Fund Advisor. This can be a family or non-family. The fund assets
                    will be distributed to a Giving Fund in the name of the Successor Advisor(s) based on the allocation
                    assigned below. You may allocate as many successors as you would like.
                  </Text.Body>

                  {successorFields.map((field, index) => (
                    <Box key={field.id} sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 2 }}>
                      <Stack spacing={2}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Text.H6>Successor Advisor {index + 1}</Text.H6>
                          <IconButton onClick={() => removeSuccessor(index)} color="error">
                            <Trash style={{ fontSize: '18px' }} />
                          </IconButton>
                        </Box>

                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2, width: '100%' }}>
                          <TextField
                            label="First Name"
                            error={!!errors.successors?.[index]?.firstName}
                            helperText={errors.successors?.[index]?.firstName?.message}
                            {...register(`successors.${index}.firstName`, {
                              required: 'First name is required',
                            })}
                            fullWidth
                          />
                          <TextField
                            label="Last Name"
                            error={!!errors.successors?.[index]?.lastName}
                            helperText={errors.successors?.[index]?.lastName?.message}
                            {...register(`successors.${index}.lastName`, {
                              required: 'Last name is required',
                            })}
                            fullWidth
                          />
                          <TextField
                            label="Email"
                            type="email"
                            error={!!errors.successors?.[index]?.email}
                            helperText={errors.successors?.[index]?.email?.message}
                            {...register(`successors.${index}.email`, {
                              required: 'Email is required',
                              pattern: {
                                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                message: 'Invalid email address',
                              },
                            })}
                            fullWidth
                          />
                          <TextField
                            label="Phone Number"
                            error={!!errors.successors?.[index]?.phoneNumber}
                            helperText={errors.successors?.[index]?.phoneNumber?.message}
                            {...register(`successors.${index}.phoneNumber`, {
                              required: 'Phone number is required',
                            })}
                            fullWidth
                          />
                        </Box>

                        <TextField
                          label="Address"
                          error={!!errors.successors?.[index]?.address}
                          helperText={errors.successors?.[index]?.address?.message}
                          {...register(`successors.${index}.address`, {
                            required: 'Address is required',
                          })}
                          fullWidth
                        />

                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2, width: '100%' }}>
                          <TextField
                            label="% of Allocation"
                            type="number"
                            error={!!errors.successors?.[index]?.allocationPercentage}
                            helperText={
                              errors.successors?.[index]?.allocationPercentage?.message ||
                              'Enter percentage (1-100, no decimals)'
                            }
                            {...register(`successors.${index}.allocationPercentage`, {
                              required: 'Allocation percentage is required',
                              min: { value: 1, message: 'Minimum allocation is 1%' },
                              max: { value: 100, message: 'Maximum allocation is 100%' },
                            })}
                            fullWidth
                            inputProps={{ min: 1, max: 100, step: 1 }}
                          />
                          <TextField
                            label="Relationship to You"
                            error={!!errors.successors?.[index]?.relationshipToYou}
                            helperText={errors.successors?.[index]?.relationshipToYou?.message}
                            {...register(`successors.${index}.relationshipToYou`, {
                              required: 'Relationship is required',
                            })}
                            fullWidth
                          />
                          <TextField
                            label="Date of Birth"
                            type="date"
                            error={!!errors.successors?.[index]?.dateOfBirth}
                            helperText={errors.successors?.[index]?.dateOfBirth?.message}
                            {...register(`successors.${index}.dateOfBirth`, {
                              required: 'Date of birth is required',
                            })}
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                          />
                        </Box>

                        <FormControl fullWidth>
                          <InputLabel>
                            If this successor cannot be reached or is previously deceased, please:
                          </InputLabel>
                          <Controller
                            name={`successors.${index}.alternativeAction`}
                            control={control}
                            rules={{ required: 'Please select an alternative action' }}
                            render={({ field: altActionField }) => (
                              <Select
                                {...altActionField}
                                label="If this successor cannot be reached or is previously deceased, please:"
                                error={!!errors.successors?.[index]?.alternativeAction}
                              >
                                <MenuItem value="allocate_pro_rata_successors">
                                  Allocate pro rata across the other successors
                                </MenuItem>
                                <MenuItem value="allocate_pro_rata_charities">
                                  Allocate pro rata across the named charities
                                </MenuItem>
                                <MenuItem value="allocate_givewise_initiatives">
                                  Allocate to GiveWise initiatives
                                </MenuItem>
                              </Select>
                            )}
                          />
                          {errors.successors?.[index]?.alternativeAction && (
                            <FormHelperText error>{errors.successors[index].alternativeAction.message}</FormHelperText>
                          )}
                        </FormControl>
                      </Stack>
                    </Box>
                  ))}

                  <Button type="button" variant="outlined" startIcon={<Plus />} onClick={addSuccessor} sx={{ mt: 2 }}>
                    {successorFields.length === 0 ? 'Add Successor Advisor' : 'Add Additional Successor Advisor'}
                  </Button>
                </Box>

                <Divider />

                {/* Fund Disposition Section */}
                <Box>
                  <Text.H5 sx={{ mb: 2 }}>Fund Disposition</Text.H5>
                  <Text.Body sx={{ mb: 3 }}>
                    You may elect to draw down the Giving Fund over a specified time period with proceeds gifted to one
                    or more registered Canadian charities based on the allocation described. You may allocate as many
                    charities as you would like.
                  </Text.Body>

                  {charityFields.map((field, index) => (
                    <Box key={field.id} sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 2 }}>
                      <Stack spacing={2}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Text.H6>Charity {index + 1}</Text.H6>
                          <IconButton onClick={() => removeCharity(index)} color="error">
                            <Trash style={{ fontSize: '18px' }} />
                          </IconButton>
                        </Box>
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2, width: '100%' }}>
                          <TextField
                            label="Charity Name"
                            error={!!errors.charities?.[index]?.charityName}
                            helperText={errors.charities?.[index]?.charityName?.message}
                            {...register(`charities.${index}.charityName`, {
                              required: 'Charity name is required',
                            })}
                            fullWidth
                          />
                          <TextField
                            label="Registered Charity Number"
                            error={!!errors.charities?.[index]?.registrationNumber}
                            helperText={errors.charities?.[index]?.registrationNumber?.message}
                            {...register(`charities.${index}.registrationNumber`, {
                              required: 'Registration number is required',
                              validate: (value) =>
                                value.length === 15 || 'Registration number must be exactly 15 characters',
                            })}
                            fullWidth
                          />
                        </Box>
                        <TextField
                          label="Designation"
                          error={!!errors.charities?.[index]?.designation}
                          helperText={
                            errors.charities?.[index]?.designation?.message ||
                            'Specify how you want the funds to be used'
                          }
                          {...register(`charities.${index}.designation`, {
                            required: 'Designation is required',
                          })}
                          fullWidth
                        />
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2, width: '100%' }}>
                          <TextField
                            label="% of Allocation"
                            type="number"
                            error={!!errors.charities?.[index]?.allocationPercentage}
                            helperText={
                              errors.charities?.[index]?.allocationPercentage?.message ||
                              'Enter percentage (1-100, no decimals)'
                            }
                            {...register(`charities.${index}.allocationPercentage`, {
                              required: 'Allocation percentage is required',
                              min: { value: 1, message: 'Minimum allocation is 1%' },
                              max: { value: 100, message: 'Maximum allocation is 100%' },
                            })}
                            fullWidth
                            inputProps={{ min: 1, max: 100, step: 1 }}
                          />

                          <FormControl fullWidth>
                            <InputLabel>Time Frame For Gift</InputLabel>
                            <Controller
                              name={`charities.${index}.timeFrame`}
                              control={control}
                              rules={{ required: 'Time frame is required' }}
                              render={({ field: timeFrameField }) => (
                                <Select
                                  {...timeFrameField}
                                  label="Time Frame For Gift"
                                  error={!!errors.charities?.[index]?.timeFrame}
                                >
                                  <MenuItem value="immediately">Immediately</MenuItem>
                                  <MenuItem value="months">Distributed over months</MenuItem>
                                  <MenuItem value="years">Distributed over years</MenuItem>
                                </Select>
                              )}
                            />
                            {errors.charities?.[index]?.timeFrame && (
                              <FormHelperText error>{errors.charities[index].timeFrame.message}</FormHelperText>
                            )}
                          </FormControl>

                          {watch(`charities.${index}.timeFrame`) === 'months' && (
                            <TextField
                              label="Number of Months"
                              type="number"
                              error={!!errors.charities?.[index]?.timeFrameMonths}
                              helperText={errors.charities?.[index]?.timeFrameMonths?.message}
                              {...register(`charities.${index}.timeFrameMonths`, {
                                required: 'Number of months is required',
                                min: { value: 1, message: 'Minimum 1 month' },
                              })}
                              fullWidth
                              inputProps={{ min: 1, step: 1 }}
                            />
                          )}

                          {watch(`charities.${index}.timeFrame`) === 'years' && (
                            <TextField
                              label="Number of Years"
                              type="number"
                              error={!!errors.charities?.[index]?.timeFrameYears}
                              helperText={errors.charities?.[index]?.timeFrameYears?.message}
                              {...register(`charities.${index}.timeFrameYears`, {
                                required: 'Number of years is required',
                                min: { value: 1, message: 'Minimum 1 year' },
                              })}
                              fullWidth
                              inputProps={{ min: 1, step: 1 }}
                            />
                          )}
                        </Box>

                        <FormControl fullWidth>
                          <InputLabel>If this charity is no longer functioning, please:</InputLabel>
                          <Controller
                            name={`charities.${index}.alternativeAction`}
                            control={control}
                            rules={{ required: 'Please select an alternative action' }}
                            render={({ field: altActionField }) => (
                              <Select
                                {...altActionField}
                                label="If this charity is no longer functioning, please:"
                                error={!!errors.charities?.[index]?.alternativeAction}
                              >
                                <MenuItem value="allocate_pro_rata_charities">
                                  Allocate pro rata across my other charity(ies)
                                </MenuItem>
                                <MenuItem value="allocate_pro_rata_successors">
                                  Allocate pro rata to my above named Successor(s)
                                </MenuItem>
                              </Select>
                            )}
                          />
                          {errors.charities?.[index]?.alternativeAction && (
                            <FormHelperText error>{errors.charities[index].alternativeAction.message}</FormHelperText>
                          )}
                        </FormControl>
                      </Stack>
                    </Box>
                  ))}

                  <Button type="button" variant="outlined" startIcon={<Plus />} onClick={addCharity} sx={{ mt: 2 }}>
                    {charityFields.length === 0 ? 'Add Charity' : 'Add Additional Charity'}
                  </Button>
                </Box>

                {/* Save and Delete */}
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 4 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={isSubmitting || (successorFields.length === 0 && charityFields.length === 0)}
                    size="large"
                  >
                    {isSubmitting ? 'Saving...' : 'Save Instructions'}
                  </Button>

                  {data?.getFundSuccessionInstructions && (
                    <Button
                      type="button"
                      variant="outlined"
                      color="error"
                      disabled={isSubmitting}
                      size="large"
                      onClick={() => setDeleteDialogOpen(true)}
                    >
                      {isSubmitting ? 'Deleting...' : 'Delete All Instructions'}
                    </Button>
                  )}
                </Box>

                <Alert {...alertProps} />

                <Text.Body>
                  For more information or if you have any questions, please email{' '}
                  <Link href="mailto:support@givewise.ca">support@givewise.ca</Link> and we will be happy to help.
                </Text.Body>
              </Stack>
            </Stack>
          </fieldset>
        </form>
      </Container>
      <NotificationDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        type="alert"
        title="Delete All Instructions"
        message="Are you sure you want to delete all succession instructions? This action cannot be undone."
        actions={[
          { label: 'Cancel', onClick: () => setDeleteDialogOpen(false) },
          { label: 'Delete', onClick: handleDeleteConfirm },
        ]}
      />
      <NotificationDialog
        open={allocationErrorOpen}
        onClose={() => setAllocationErrorOpen(false)}
        type="error"
        title="Validation Error"
        message="The % of Allocation for all Successor Advisors and Charities must equal 100%."
        actions={[{ label: 'Close', onClick: () => setAllocationErrorOpen(false) }]}
      />
    </CardBox>
  )
}
